import type { Message } from './Communication';

import { AsyncListenableEvent } from '@egr/wcf/modules/utils';
import { addBreadcrumb } from '@egr/xbox/utils/Error';
import { getEnvBoolean } from '../utils/ReactScriptHelper';
import { DebugLogFile } from './DebugLogFile';
import { registerHandler } from './MessageManager';

type ActionTypeGuard<T extends string> = (value: string) => value is T;
function getTypeGuard<T extends string>(allowedValues: Array<string>): ActionTypeGuard<T> {
    return (value: string): value is T => {
        return allowedValues.indexOf(value) !== -1;
    };
}

function getFuzzyTypeGuard<T extends string>(allowedValues: Array<string>): ActionTypeGuard<T> {
    return (value: string): value is T => {
        for (const allowedValue of allowedValues) {
            if (value.startsWith(allowedValue)) {
                return true;
            }
        }
        return false;
    };
}

type SynchronizeAction = 'background' | 'foreground';
export type UserinteractionAction = 'back_pressed';
export interface OpenfileAction {
    error: boolean;
    type: 'pec' | 'pbox' | 'obk' | 'obx';
    path: string;
    context: string;
}

type ActionTypeGuardSignature<T extends string> = (value: string) => value is T;
const isSynchronizeAction: ActionTypeGuardSignature<SynchronizeAction> = getTypeGuard<SynchronizeAction>(['background', 'foreground']);
const isUserinteractionAction: ActionTypeGuardSignature<UserinteractionAction> = getFuzzyTypeGuard<UserinteractionAction>(['back_pressed', 'accessory_button']);

interface WebActionEvents {
    openfile: AsyncListenableEvent<OpenfileAction, undefined>;
    synchronize: AsyncListenableEvent<SynchronizeAction, undefined>;
    userinteraction: AsyncListenableEvent<UserinteractionAction, undefined>;
    arUpdate: AsyncListenableEvent<string, undefined>;
    arPlanesAdded: AsyncListenableEvent<string, undefined>;
    arPlanesUpdated: AsyncListenableEvent<string, undefined>;
    arPlanesRemoved: AsyncListenableEvent<string, undefined>;
    arClose: AsyncListenableEvent<string, undefined>;
}

export const events: WebActionEvents = {
    openfile: new AsyncListenableEvent<OpenfileAction, undefined>(undefined),
    synchronize: new AsyncListenableEvent<SynchronizeAction, undefined>(undefined),
    userinteraction: new AsyncListenableEvent<UserinteractionAction, undefined>(undefined),
    arUpdate: new AsyncListenableEvent<string, undefined>(undefined),
    arPlanesAdded: new AsyncListenableEvent<string, undefined>(undefined),
    arPlanesUpdated: new AsyncListenableEvent<string, undefined>(undefined),
    arPlanesRemoved: new AsyncListenableEvent<string, undefined>(undefined),
    arClose: new AsyncListenableEvent<string, undefined>(undefined),
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type WebActionHandler<T = any> = (event: MessageEvent<Message<T>>) => void;

const RegisteredHandlers: Map<string, Set<WebActionHandler>> = new Map<string, Set<WebActionHandler>>();

function webactionMessageHandler(event: MessageEvent): void {
    const handlers: Set<WebActionHandler> | undefined = RegisteredHandlers.get(event.data.action);

    addBreadcrumb({
        message: event.data.action,
        category: 'webAction',
        data: {
            hasHandlers: handlers != null
        }
    });

    if (handlers != null) {
        for (const handler of handlers) {
            handler(event);
        }
    }
}

export function registerWebAction<T>(name: string, handler: WebActionHandler<T>): VoidFunction {
    if (!RegisteredHandlers.has(name)) {
        RegisteredHandlers.set(name, new Set());
    }

    RegisteredHandlers.get(name)!.add(handler);

    return (): void => {
        RegisteredHandlers.get(name)?.delete(handler);
    };
}

registerHandler({
    type: 'webaction',
    callback: webactionMessageHandler
});

registerWebAction(
    'openfile',
    (event: MessageEvent): void => {
        void events.openfile.trigger(event.data.arguments);
    }
);

registerWebAction(
    'synchronize',
    (event: MessageEvent): void => {
        if (isSynchronizeAction(event.data.arguments)) {
            void events.synchronize.trigger(event.data.arguments);
        }
    }
);

registerWebAction(
    'userinteraction',
    (event: MessageEvent): void => {
        if (isUserinteractionAction(event.data.arguments)) {
            void events.userinteraction.trigger(event.data.arguments);
        }
    }
);

type ArEventName = 'arUpdate' | 'arPlanesAdded' | 'arPlanesUpdated' | 'arPlanesRemoved' | 'arClose';

const arLogFile = getEnvBoolean('LOG_AR_EVENTS') ? new DebugLogFile(`/tmp/ar.${Date.now()}.log`, 1024) : undefined;
function getArEventHandler(eventName: ArEventName): WebActionHandler {
    return (event: MessageEvent): void => {
        void events[eventName].trigger(event.data.data);
        void arLogFile?.log(JSON.stringify(event.data) + '\n');
    };
}

registerWebAction('ar_update', getArEventHandler('arUpdate'));
registerWebAction('ar_planesadded', getArEventHandler('arPlanesAdded'));
registerWebAction('ar_planesupdated', getArEventHandler('arPlanesUpdated'));
registerWebAction('ar_planesremoved', getArEventHandler('arPlanesRemoved'));
registerWebAction('ar_closed', getArEventHandler('arClose'));

export default events;